import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiEye, FiEyeOff, FiLock } from 'react-icons/fi';
import { DContext } from '../../context/DataContext';
import Logo from '../../assets/Logo bg.svg';
import { translateAuthServerMessage } from '../../utils/authServerMessage';
import MetaData from '../../utils/MetaData';

const ResetPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { BeURL, setLoader } = useContext(DContext);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
  }, [i18n.language]);

  const passwordError = useMemo(() => {
    if (!password) return '';
    const value = password.trim();
    if (value.length < 8) return t('auth.validations.passwordMinLength');
    if (!/[A-Z]/.test(value)) return t('auth.validations.passwordUppercase');
    if (!/[a-z]/.test(value)) return t('auth.validations.passwordLowercase');
    if (!/\d/.test(value)) return t('auth.validations.passwordNumber');
    if (!/[!@#$%^&*]/.test(value)) return t('auth.validations.passwordSpecial');
    return '';
  }, [password, t]);

  const confirmPasswordError = useMemo(() => {
    if (!confirmPassword) return '';
    return password === confirmPassword ? '' : t('auth.validations.passwordMismatch');
  }, [password, confirmPassword, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!token) nextErrors.token = t('auth.reset.invalidToken');
    if (!password.trim()) nextErrors.password = t('auth.validations.passwordRequired');
    if (!confirmPassword.trim()) nextErrors.confirmPassword = t('auth.validations.confirmPasswordRequired');
    if (passwordError) nextErrors.password = passwordError;
    if (confirmPasswordError) nextErrors.confirmPassword = confirmPasswordError;

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setLoader(true);
      const res = await fetch(`${BeURL}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, password })
      });
      const data = await res.json();
      setLoader(false);

      if (!data.success) {
        alert(translateAuthServerMessage(data.message, t, 'auth.messages.resetFailed'));
        return;
      }

      alert(t('auth.messages.resetSuccess'));
      navigate('/login', { replace: true });
    } catch (err) {
      setLoader(false);
      console.log('Error in reset-password:', err);
      alert(t('auth.messages.serverError'));
    }
  };

  return (
    <>
      <MetaData
        title='Reset Password | Be Alright Krabi'
        description='Reset your Be Alright Krabi account password.'
        canonicalUrl={`${window.location.origin}/reset-password`}
        robots='noindex,nofollow'
      />
      <div className="relative flex min-h-[85vh] flex-wrap items-center justify-center gap-6 px-4 py-10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(23,57,189,0.12),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.12),_transparent_45%)]" />

        <section className="relative w-full max-w-lg text-center">
          <img src={Logo} alt="logo" className="mx-auto h-28 w-auto sm:h-36" />
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#1739bd] sm:text-5xl">{t('auth.reset.title')}</h1>
          <p className="mt-3 text-sm text-slate-600 sm:text-base">{t('auth.reset.subtitle')}</p>
        </section>

        <section className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.12)] backdrop-blur sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.token && <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{errors.token}</p>}

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.newPassword')}</span>
              <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                <FiLock className="text-slate-500" />
                <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? 'text' : 'password'} className="w-full bg-transparent text-sm outline-none" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="text-slate-500 transition hover:text-[#1739bd]">
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {(errors.password || passwordError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.password || passwordError}</p>}
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.confirmPassword')}</span>
              <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                <FiLock className="text-slate-500" />
                <input value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} type={showPassword ? 'text' : 'password'} className="w-full bg-transparent text-sm outline-none" placeholder="••••••••" />
              </div>
              {(errors.confirmPassword || confirmPasswordError) && <p className="mt-1 text-xs font-medium text-rose-600">{errors.confirmPassword || confirmPasswordError}</p>}
            </label>

            <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
              <span>{t('auth.reset.submit')}</span>
              <FiArrowRight />
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-slate-600">
            <Link to="/login" className="font-semibold text-[#1739bd] no-underline hover:text-indigo-700">{t('auth.reset.backToLogin')}</Link>
          </p>
        </section>
      </div>
    </>
  );
};

export default ResetPassword;
